import * as React from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useNotify } from 'react-admin';
import { Link } from 'react-router-dom';

import { apiFetch } from '../api/httpClient';

type SelfProfile = {
    id: string;
    username: string;
    fullName: string;
    email?: string | null;
    profileImage?: string | null;
};

export function SelfProfilePage() {
    const notify = useNotify();
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [row, setRow] = React.useState<SelfProfile | null>(null);
    const [fullName, setFullName] = React.useState('');
    const [currentPassword, setCurrentPassword] = React.useState('');
    const [newPassword, setNewPassword] = React.useState('');

    React.useEffect(() => {
        let alive = true;
        void (async () => {
            try {
                const res = await apiFetch('/api/auth/self-profile', { method: 'GET' }, { auth: true });
                if (!alive) return;
                if (!res.ok) {
                    notify('Could not load profile', { type: 'warning' });
                    return;
                }
                const j = (await res.json()) as SelfProfile;
                setRow(j);
                setFullName(j.fullName ?? '');
            } catch {
                if (alive) notify('Could not load profile', { type: 'warning' });
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [notify]);

    const save = async () => {
        setSaving(true);
        try {
            const body: Record<string, unknown> = { fullName: fullName.trim() };
            if (newPassword.trim().length > 0) {
                body.currentPassword = currentPassword;
                body.newPassword = newPassword;
            }
            const res = await apiFetch(
                '/api/auth/self-profile',
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                },
                { auth: true }
            );
            const text = await res.text();
            let j: any = {};
            try {
                j = text ? JSON.parse(text) : {};
            } catch {
                j = {};
            }
            if (!res.ok) {
                notify(String(j.message ?? res.statusText ?? 'Save failed'), { type: 'warning' });
                return;
            }
            notify('Profile saved', { type: 'success' });
            setRow(j as SelfProfile);
            setCurrentPassword('');
            setNewPassword('');
        } catch (e: any) {
            notify(e?.message ?? 'Save failed', { type: 'warning' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Loading…</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 480, mx: 'auto', p: 3 }}>
            <Typography variant="h5" gutterBottom>
                My profile
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Username: {row?.username ?? '—'}
            </Typography>
            <TextField
                label="Full name"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                fullWidth
                margin="normal"
            />
            <TextField
                label="Current password (required to change password)"
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                fullWidth
                margin="normal"
                autoComplete="current-password"
            />
            <TextField
                label="New password"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                fullWidth
                margin="normal"
                autoComplete="new-password"
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                <Button variant="contained" onClick={() => void save()} disabled={saving}>
                    Save
                </Button>
                <Button component={Link} to="/license-blocked" variant="outlined">
                    Back
                </Button>
            </Box>
        </Box>
    );
}
